import type { BoundingBox, Position } from '@rio-cloud/rio-uikit/mapTypes';

import type { TourStop } from './TourStop.ts';

export type TourStopMapRole = 'start' | 'intermediate' | 'destination' | 'start-destination';

export type TourStopMapItem = {
    id: string;
    position: Position;
    role: TourStopMapRole;
    sequence: number;
};

export type TourStopMapSegment = {
    from: Position;
    id: string;
    to: Position;
};

const MINIMUM_PADDING = 0.01;
const PADDING_RATIO = 0.1;

const parsePosition = (stop: TourStop): Position | undefined => {
    if (
        stop.latitude == null ||
        stop.longitude == null ||
        stop.latitude.trim() === '' ||
        stop.longitude.trim() === ''
    ) {
        return undefined;
    }

    const lat = Number(stop.latitude);
    const lng = Number(stop.longitude);

    if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return undefined;
    }

    return { lat, lng };
};

export const getTourStopMapRole = (index: number, stopCount: number): TourStopMapRole => {
    if (stopCount === 1) {
        return 'start-destination';
    }
    if (index === 0) {
        return 'start';
    }
    if (index === stopCount - 1) {
        return 'destination';
    }
    return 'intermediate';
};

export const getTourStopMapItems = (stops: TourStop[]): TourStopMapItem[] =>
    stops.flatMap((stop, index) => {
        const position = parsePosition(stop);
        return position
            ? [
                  {
                      id: stop.id,
                      position,
                      role: getTourStopMapRole(index, stops.length),
                      sequence: index + 1,
                  },
              ]
            : [];
    });

export const getTourStopMapSegments = (items: TourStopMapItem[]): TourStopMapSegment[] =>
    items.flatMap((item, index) => {
        const nextItem = items[index + 1];
        if (!nextItem || nextItem.sequence !== item.sequence + 1) {
            return [];
        }

        return [
            {
                id: `${item.id}-${nextItem.id}`,
                from: item.position,
                to: nextItem.position,
            },
        ];
    });

export const getTourStopMapBounds = (items: Pick<TourStopMapItem, 'position'>[]): BoundingBox | undefined => {
    if (items.length === 0) {
        return undefined;
    }

    let minLatitude = 90;
    let maxLatitude = -90;
    let minLongitude = 180;
    let maxLongitude = -180;
    for (const { position } of items) {
        minLatitude = Math.min(minLatitude, position.lat);
        maxLatitude = Math.max(maxLatitude, position.lat);
        minLongitude = Math.min(minLongitude, position.lng);
        maxLongitude = Math.max(maxLongitude, position.lng);
    }
    const latitudePadding = Math.max((maxLatitude - minLatitude) * PADDING_RATIO, MINIMUM_PADDING);
    const longitudePadding = Math.max((maxLongitude - minLongitude) * PADDING_RATIO, MINIMUM_PADDING);

    return {
        top: Math.min(90, maxLatitude + latitudePadding),
        bottom: Math.max(-90, minLatitude - latitudePadding),
        left: Math.max(-180, minLongitude - longitudePadding),
        right: Math.min(180, maxLongitude + longitudePadding),
    };
};
